import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession, setSelectedAdminSession } from "../utils/adminSession";

const API = "/backend/public/api";
const uploadsBase = "/uploads";
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 3000 };
const modalStyle = { background: "#fff", width: "min(1100px, 100%)", maxHeight: "92vh", overflow: "auto", borderRadius: "22px", padding: "24px", boxShadow: "0 30px 70px rgba(15,23,42,0.28)" };

const toDateInputValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!match) return "";

  return `${match[3]}-${match[2]}-${match[1]}`;
};

function OurStudentsAdmin() {
  const [rows, setRows] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(() => getSelectedAdminSession());
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editFiles, setEditFiles] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/academic-sessions`);
      const sessionRows = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
      setSessions(sessionRows.map((item) => item.session_name));
    } catch (error) {
      setSessions([]);
    }
  };

  const loadRows = useCallback(async (sessionName = selectedSession) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student-applications/approved`, {
        params: {
          workflow_stage: "all",
          session_year: sessionName === "All Sessions" ? "all" : sessionName,
          _ts: Date.now(),
        },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSession]);

  useEffect(() => {
    loadSessions();
    const syncSession = () => setSelectedSession(getSelectedAdminSession());
    window.addEventListener(ADMIN_SESSION_EVENT, syncSession);
    return () => window.removeEventListener(ADMIN_SESSION_EVENT, syncSession);
  }, []);

  useEffect(() => {
    loadRows(selectedSession);
  }, [loadRows, selectedSession]);

  const summary = useMemo(() => ({
    total: rows.length,
    certified: rows.filter((row) => row.workflow_stage === "certified").length,
    registered: rows.filter((row) => row.workflow_stage === "registered").length,
  }), [rows]);

  const openEdit = async (id) => {
    try {
      const res = await axios.get(`${API}/student-applications/${id}`);
      const item = res.data;
      const courseRes = item?.franchise_id
        ? await axios.get(`${API}/franchise-portal/${item.franchise_id}/courses`, {
            params: { _ts: Date.now() },
          })
        : { data: [] };

      setEditingItem(item);
      setCourseOptions(Array.isArray(courseRes.data) ? courseRes.data : []);
      setEditForm({
        student_name: item.student_name || "",
        phone: item.phone || "",
        email: item.email || "",
        dob: toDateInputValue(item.dob),
        session_year: item.session_year || "",
        father_name: item.father_name || "",
        admission_date: toDateInputValue(item.admission_date),
        guardian_name: item.guardian_name || "",
        address: item.address || "",
        course_id: item.course_id || "",
      });
      setEditFiles({});
    } catch (error) {
      window.alert(error.response?.data?.message || "Edit data load failed");
    }
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFile = (event) => {
    const { name, files } = event.target;
    setEditFiles((prev) => ({ ...prev, [name]: files?.[0] || null }));
  };

  const saveEdit = async () => {
    if (!editingItem?.id) return;

    try {
      setSavingEdit(true);
      const payload = new FormData();
      Object.entries(editForm).forEach(([key, value]) => payload.append(key, value || ""));
      Object.entries(editFiles).forEach(([key, value]) => {
        if (value) payload.append(key, value);
      });
      await axios.post(`${API}/student-applications/${editingItem.id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditingItem(null);
      setCourseOptions([]);
      await loadRows(selectedSession);
    } catch (error) {
      window.alert(error.response?.data?.message || "Student update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <h3 className="mb-1">Our Student</h3>
            <p className="text-muted mb-0">Session-wise total registered and passout student history is stored here for master admin.</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <select className="form-select" value={selectedSession} onChange={(event) => { setSelectedSession(event.target.value); setSelectedAdminSession(event.target.value); }} style={{ minWidth: "220px" }}>
              <option value="All Sessions">All Sessions</option>
              {sessions.map((session) => (
                <option key={session} value={session}>
                  {session}
                </option>
              ))}
            </select>
            <button className="btn btn-dark" onClick={() => loadRows(selectedSession)} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-md-4"><div className="bg-white border rounded-4 shadow-sm p-3"><div className="text-muted small fw-bold">Total Student</div><div className="fs-3 fw-bold">{summary.total}</div></div></div>
          <div className="col-md-4"><div className="bg-white border rounded-4 shadow-sm p-3"><div className="text-muted small fw-bold">Registered Student</div><div className="fs-3 fw-bold">{summary.registered}</div></div></div>
          <div className="col-md-4"><div className="bg-white border rounded-4 shadow-sm p-3"><div className="text-muted small fw-bold">Passout Student</div><div className="fs-3 fw-bold">{summary.certified}</div></div></div>
        </div>

        <div className="table-responsive bg-white shadow-sm rounded-4 p-2">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Registration No</th>
                <th>Student</th>
                <th>Course</th>
                <th>Session</th>
                <th>Study Centre</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td>{row.registration_no || "-"}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <img src={row.photo ? `${uploadsBase}/${row.photo}` : "https://placehold.co/52x52/e2e8f0/475569?text=ST"} alt={row.student_name} style={{ width: "52px", height: "52px", borderRadius: "14px", objectFit: "cover", border: "1px solid #e2e8f0" }} />
                      <div>
                        <div className="fw-semibold">{row.student_name}</div>
                        <div className="text-muted small">{row.application_no || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td>{row.course_name || "-"}</td>
                  <td>{row.session_year || "-"}</td>
                  <td>{row.study_centre_name || "-"}</td>
                  <td className="text-capitalize">{String(row.workflow_stage || "-").replaceAll("_", " ")}</td>
                  <td className="text-capitalize">{row.status || "-"}</td>
                  <td>
                    <button
                      type="button"
                      title="Edit"
                      aria-label="Edit"
                      className="btn btn-sm"
                      onClick={() => openEdit(row.id)}
                      style={{
                        border: "none",
                        borderRadius: "10px",
                        padding: "8px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "36px",
                        height: "36px",
                        background: "#e2e8f0",
                        color: "#0f172a",
                      }}
                    >
                      <i className="bi bi-pen"></i>
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">No student found for this session.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-muted">Loading students...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingItem ? (
        <div style={overlayStyle} onClick={() => { setEditingItem(null); setCourseOptions([]); }}>
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-1">Edit Student</h3>
                <p className="text-muted mb-0">Master admin can edit full student data from the Our Student page.</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => { setEditingItem(null); setCourseOptions([]); }}>
                Close
              </button>
            </div>

            <div className="row g-3">
              <div className="col-md-6"><label className="form-label">Student Name</label><input className="form-control" name="student_name" value={editForm.student_name || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Phone</label><input className="form-control" name="phone" value={editForm.phone || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Email</label><input className="form-control" name="email" value={editForm.email || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">DOB</label><input type="date" className="form-control" name="dob" value={editForm.dob || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6">
                <label className="form-label">Session</label>
                <select className="form-select" name="session_year" value={editForm.session_year || ""} onChange={handleEditChange}>
                  <option value="">Select Session</option>
                  {sessions.map((session) => (
                    <option key={session} value={session}>
                      {session}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6"><label className="form-label">Father Name</label><input className="form-control" name="father_name" value={editForm.father_name || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Admission Date</label><input type="date" className="form-control" name="admission_date" value={editForm.admission_date || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6"><label className="form-label">Guardian Name</label><input className="form-control" name="guardian_name" value={editForm.guardian_name || ""} onChange={handleEditChange} /></div>
              <div className="col-md-6">
                <label className="form-label">Course</label>
                <select className="form-select" name="course_id" value={editForm.course_id || ""} onChange={handleEditChange}>
                  <option value="">Select Course</option>
                  {courseOptions.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12"><label className="form-label">Address</label><textarea className="form-control" rows="4" name="address" value={editForm.address || ""} onChange={handleEditChange}></textarea></div>
              {["photo", "signature", "id_proof", "academic_document"].map((field) => (
                <div className="col-md-6" key={field}>
                  <label className="form-label text-capitalize">{field.replaceAll("_", " ")}</label>
                  <input className="form-control" type="file" name={field} onChange={handleEditFile} />
                </div>
              ))}
            </div>

            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? "Saving..." : "Save Edit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default OurStudentsAdmin;



